import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';

export function CollectionOperations() {
  const [tab, setTab] = useState<
    'promises' | 'letters' | 'contacts' | 'notes' | 'verifications' | 'legal'
  >('promises');
  const [loans, setLoans] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [promises, setPromises] = useState<any[]>([]);
  const [letters, setLetters] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [verifications, setVerifications] = useState<any[]>([]);
  const [legalCandidates, setLegalCandidates] = useState<any[]>([]);
  const [legalLoans, setLegalLoans] = useState<any[]>([]);
  const [legalThreshold, setLegalThreshold] = useState<number>(90);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [promiseForm, setPromiseForm] = useState({ loanId: '', promisedAmount: '', promisedDate: '', notes: '' });
  const [letterForm, setLetterForm] = useState({ loanId: '', letterType: 'COBRANZA', notes: '' });
  const [contactForm, setContactForm] = useState({ customerId: '', loanId: '', channel: 'LLAMADO', outcome: '', notes: '' });
  const [verificationForm, setVerificationForm] = useState({ customerId: '', loanId: '', employerName: '' });

  const load = async () => {
    try {
      const [ls, cs, ps, lt, ct, candidates, legal, threshold, ns, vs] = await Promise.all([
        apiService.getLoans(),
        apiService.getCustomers(),
        apiService.listPaymentPromises(),
        apiService.listCollectionLetters(),
        apiService.listCollectionContacts(),
        apiService.listLegalCandidates(),
        apiService.listLegalLoans(),
        apiService.getLegalDaysThreshold(),
        apiService.listCollectionNotes(),
        apiService.listEmploymentVerifications(),
      ]);
      setLoans(ls);
      setCustomers(cs);
      setPromises(ps);
      setLetters(lt);
      setContacts(ct);
      setLegalCandidates(candidates);
      setLegalLoans(legal);
      setLegalThreshold(threshold.days);
      setNotes(ns);
      setVerifications(vs);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const submitPromise = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiService.createPaymentPromise({
        loanId: promiseForm.loanId,
        promisedAmount: parseFloat(promiseForm.promisedAmount),
        promisedDate: promiseForm.promisedDate,
        notes: promiseForm.notes || undefined,
      });
      setPromiseForm({ loanId: '', promisedAmount: '', promisedDate: '', notes: '' });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitLetter = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiService.createCollectionLetter({
        loanId: letterForm.loanId,
        letterType: letterForm.letterType,
        status: 'SENT',
        notes: letterForm.notes || undefined,
      });
      setLetterForm({ loanId: '', letterType: 'COBRANZA', notes: '' });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitContact = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiService.createCollectionContact({
        customerId: contactForm.customerId,
        loanId: contactForm.loanId || undefined,
        channel: contactForm.channel,
        outcome: contactForm.outcome || undefined,
        notes: contactForm.notes || undefined,
      });
      setContactForm({ customerId: '', loanId: '', channel: 'LLAMADO', outcome: '', notes: '' });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiService.createEmploymentVerification({
        customerId: verificationForm.customerId,
        loanId: verificationForm.loanId || undefined,
        employerName: verificationForm.employerName || undefined,
      });
      setVerificationForm({ customerId: '', loanId: '', employerName: '' });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const updatePromiseStatus = async (id: string, status: string) => {
    setBusy(true);
    try {
      await apiService.updatePaymentPromiseStatus(id, status);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const tabLabels: Record<typeof tab, string> = {
    promises: 'Promesas',
    letters: 'Cartas',
    contacts: 'Contactos',
    notes: 'Novedades',
    verifications: 'Verificaciones',
    legal: 'Jurídico',
  };

  return (
    <div className="mt-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Gestión operativa</h2>
      {error && <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="flex gap-2 mb-4 flex-wrap">
        {(['promises', 'letters', 'contacts', 'notes', 'verifications', 'legal'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded-md text-sm ${tab === t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            {tabLabels[t]}
          </button>
        ))}
      </div>

      {tab === 'promises' && (
        <div className="grid md:grid-cols-2 gap-6">
          <form onSubmit={submitPromise} className="bg-white border rounded-lg p-4 space-y-3">
            <h3 className="font-medium">Nueva promesa de pago</h3>
            <select required value={promiseForm.loanId} onChange={(e) => setPromiseForm({ ...promiseForm, loanId: e.target.value })} className="w-full border rounded-md px-3 py-2">
              <option value="">Préstamo…</option>
              {loans.map((l) => <option key={l.id} value={l.id}>#{l.loanNumber ?? l.id.slice(0, 8)} — ${l.amount}</option>)}
            </select>
            <input required type="number" step="0.01" placeholder="Monto prometido" value={promiseForm.promisedAmount} onChange={(e) => setPromiseForm({ ...promiseForm, promisedAmount: e.target.value })} className="w-full border rounded-md px-3 py-2" />
            <input required type="date" value={promiseForm.promisedDate} onChange={(e) => setPromiseForm({ ...promiseForm, promisedDate: e.target.value })} className="w-full border rounded-md px-3 py-2" />
            <textarea placeholder="Notas" value={promiseForm.notes} onChange={(e) => setPromiseForm({ ...promiseForm, notes: e.target.value })} className="w-full border rounded-md px-3 py-2" rows={2} />
            <button type="submit" disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Registrar promesa</button>
          </form>
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-medium mb-2">Promesas recientes</h3>
            <ul className="space-y-2 text-sm">
              {promises.slice(0, 10).map((p) => (
                <li key={p.id} className="border-b pb-2 flex justify-between gap-2">
                  <span>
                    Préstamo {p.loan?.loanNumber ?? p.loanId} — ${p.promisedAmount} — {p.promisedDate?.slice?.(0, 10)} —{' '}
                    <span className="font-medium">{p.status}</span>
                  </span>
                  {p.status === 'PENDING' && (
                    <span className="flex gap-1 shrink-0">
                      {(['KEPT', 'BROKEN', 'CANCELLED'] as const).map((st) => (
                        <button
                          key={st}
                          type="button"
                          disabled={busy}
                          onClick={() => updatePromiseStatus(p.id, st)}
                          className="text-xs text-blue-700 underline disabled:opacity-50"
                        >
                          {st}
                        </button>
                      ))}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'letters' && (
        <div className="grid md:grid-cols-2 gap-6">
          <form onSubmit={submitLetter} className="bg-white border rounded-lg p-4 space-y-3">
            <h3 className="font-medium">Nueva carta</h3>
            <select required value={letterForm.loanId} onChange={(e) => setLetterForm({ ...letterForm, loanId: e.target.value })} className="w-full border rounded-md px-3 py-2">
              <option value="">Préstamo…</option>
              {loans.map((l) => <option key={l.id} value={l.id}>#{l.loanNumber ?? l.id.slice(0, 8)}</option>)}
            </select>
            <input required value={letterForm.letterType} onChange={(e) => setLetterForm({ ...letterForm, letterType: e.target.value })} className="w-full border rounded-md px-3 py-2" placeholder="Tipo de carta" />
            <textarea placeholder="Notas" value={letterForm.notes} onChange={(e) => setLetterForm({ ...letterForm, notes: e.target.value })} className="w-full border rounded-md px-3 py-2" rows={2} />
            <button type="submit" disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Registrar carta</button>
          </form>
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-medium mb-2">Cartas recientes</h3>
            <ul className="space-y-2 text-sm">
              {letters.slice(0, 10).map((l) => (
                <li key={l.id} className="border-b pb-2">{l.letterType} — {l.loan?.loanNumber} — {l.status}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'contacts' && (
        <div className="grid md:grid-cols-2 gap-6">
          <form onSubmit={submitContact} className="bg-white border rounded-lg p-4 space-y-3">
            <h3 className="font-medium">Nueva gestión de contacto</h3>
            <select required value={contactForm.customerId} onChange={(e) => setContactForm({ ...contactForm, customerId: e.target.value })} className="w-full border rounded-md px-3 py-2">
              <option value="">Cliente…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
            </select>
            <select value={contactForm.loanId} onChange={(e) => setContactForm({ ...contactForm, loanId: e.target.value })} className="w-full border rounded-md px-3 py-2">
              <option value="">Préstamo (opcional)</option>
              {loans.map((l) => <option key={l.id} value={l.id}>#{l.loanNumber ?? l.id.slice(0, 8)}</option>)}
            </select>
            <input required value={contactForm.channel} onChange={(e) => setContactForm({ ...contactForm, channel: e.target.value })} className="w-full border rounded-md px-3 py-2" placeholder="Canal (LLAMADO, SMS…)" />
            <input value={contactForm.outcome} onChange={(e) => setContactForm({ ...contactForm, outcome: e.target.value })} className="w-full border rounded-md px-3 py-2" placeholder="Resultado" />
            <textarea placeholder="Notas" value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} className="w-full border rounded-md px-3 py-2" rows={2} />
            <button type="submit" disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Registrar contacto</button>
          </form>
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-medium mb-2">Contactos recientes</h3>
            <ul className="space-y-2 text-sm">
              {contacts.slice(0, 10).map((c) => (
                <li key={c.id} className="border-b pb-2">{c.customer?.firstName} — {c.channel} — {c.outcome ?? '—'}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'notes' && (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-medium mb-2">Novedades recientes ({notes.length})</h3>
          <ul className="space-y-2 text-sm max-h-96 overflow-y-auto">
            {notes.slice(0, 50).map((n) => (
              <li key={n.id} className="border-b pb-2">
                <span className="text-gray-500">{n.createdAt?.slice?.(0, 10)}</span> —{' '}
                {n.customer?.lastName}, {n.customer?.firstName} — [{n.type}] {n.description}
                <div className="text-xs text-gray-400">
                  Préstamo #{n.loan?.loanNumber ?? n.loanId?.slice(0, 8)} · {n.createdBy?.firstName} {n.createdBy?.lastName}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === 'verifications' && (
        <div className="grid md:grid-cols-2 gap-6">
          <form onSubmit={submitVerification} className="bg-white border rounded-lg p-4 space-y-3">
            <h3 className="font-medium">Nueva verificación laboral</h3>
            <select
              required
              value={verificationForm.customerId}
              onChange={(e) => setVerificationForm({ ...verificationForm, customerId: e.target.value })}
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="">Cliente…</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.firstName} {c.lastName}
                </option>
              ))}
            </select>
            <select
              value={verificationForm.loanId}
              onChange={(e) => setVerificationForm({ ...verificationForm, loanId: e.target.value })}
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="">Préstamo (opcional)</option>
              {loans.map((l) => (
                <option key={l.id} value={l.id}>
                  #{l.loanNumber ?? l.id.slice(0, 8)}
                </option>
              ))}
            </select>
            <input
              value={verificationForm.employerName}
              onChange={(e) => setVerificationForm({ ...verificationForm, employerName: e.target.value })}
              className="w-full border rounded-md px-3 py-2"
              placeholder="Empleador"
            />
            <button type="submit" disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
              Registrar verificación
            </button>
          </form>
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-medium mb-2">Verificaciones ({verifications.length})</h3>
            <ul className="space-y-2 text-sm max-h-96 overflow-y-auto">
              {verifications.slice(0, 30).map((v) => (
                <li key={v.id} className="border-b pb-2 flex justify-between gap-2">
                  <div>
                    {v.customer?.lastName} — {v.status}
                    {v.employerName && <span className="text-gray-500"> · {v.employerName}</span>}
                  </div>
                  {v.status === 'PENDING' && (
                    <span className="flex gap-1 shrink-0">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            await apiService.updateEmploymentVerification(v.id, { status: 'VERIFIED', result: 'OK' });
                            await load();
                          } catch (e: any) {
                            setError(e?.response?.data?.message ?? e.message);
                          } finally {
                            setBusy(false);
                          }
                        }}
                        className="text-xs text-green-700 underline"
                      >
                        OK
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            await apiService.updateEmploymentVerification(v.id, { status: 'FAILED', result: 'No verificado' });
                            await load();
                          } catch (e: any) {
                            setError(e?.response?.data?.message ?? e.message);
                          } finally {
                            setBusy(false);
                          }
                        }}
                        className="text-xs text-red-700 underline"
                      >
                        Falló
                      </button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'legal' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-700">
              Umbral de mora para derivación automática: <strong>{legalThreshold} días</strong>
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  const r = await apiService.runLegalEscalation();
                  alert(`Escalados: ${r.escalated}, omitidos: ${r.skipped}`);
                  await load();
                } catch (e: any) {
                  setError(e?.response?.data?.message ?? e.message);
                } finally {
                  setBusy(false);
                }
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:opacity-50"
            >
              Ejecutar escalado automático
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-medium mb-3">Candidatos a jurídico ({legalCandidates.length})</h3>
              {legalCandidates.length === 0 ? (
                <p className="text-sm text-gray-500">No hay candidatos con mora superior al umbral.</p>
              ) : (
                <ul className="space-y-3 text-sm">
                  {legalCandidates.map((c) => (
                    <li key={c.loanId} className="flex justify-between items-start border-b pb-2">
                      <div>
                        <div className="font-medium">#{c.loanNumber ?? c.loanId.slice(0, 8)}</div>
                        <div className="text-gray-600">{c.customer?.firstName} {c.customer?.lastName}</div>
                        <div className="text-xs text-gray-500">{c.daysOverdue} días · {c.overdueInstallments} cuotas</div>
                      </div>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            await apiService.escalateToLegal(c.loanId);
                            await load();
                          } catch (e: any) {
                            setError(e?.response?.data?.message ?? e.message);
                          } finally {
                            setBusy(false);
                          }
                        }}
                        className="text-red-700 hover:underline text-xs disabled:opacity-50"
                      >
                        Escalar
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-medium mb-3">En jurídico ({legalLoans.length})</h3>
              {legalLoans.length === 0 ? (
                <p className="text-sm text-gray-500">Ningún préstamo derivado aún.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {legalLoans.map((l) => (
                    <li key={l.id} className="border-b pb-2">
                      <div className="font-medium">#{l.loanNumber ?? l.id.slice(0, 8)} — {l.status}</div>
                      <div className="text-gray-600">{l.customer?.firstName} {l.customer?.lastName}</div>
                      <div className="text-xs text-gray-500">Desde {l.sentToLegalAt?.slice?.(0, 10)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
